import { entityHydrationRegistry } from "@/lib/kernel/entity-hydration/registry";
import type {
  Contribution,
  PrimitiveKind,
  ReducerInput,
  ReducerOutput,
} from "@/lib/kernel/entity-hydration/types";
import {
  buildHydrationPredicateContext,
  classifyPredicate,
  evaluate,
} from "@/lib/kernel/predicates";
import { DND5E_ABILITY_SCORES } from "@/lib/rules/dnd5e/vocabulary/ability-score";
import type { AbilityScore } from "./types";

const STAT_CAP_OVERRIDE = "stat-cap-override" as PrimitiveKind;
export const DEFAULT_ABILITY_SCORE_CAP = 20;
export const DEFAULT_MAX_ATTUNED_SLOTS = 3;

const defaultAbilityScoreCaps = (): Record<AbilityScore, number> =>
  Object.fromEntries(
    DND5E_ABILITY_SCORES.map((ability) => [ability, DEFAULT_ABILITY_SCORE_CAP])
  ) as Record<AbilityScore, number>;

const passesWhen = (
  input: ReducerInput<"dnd5e">,
  c: Extract<Contribution<"dnd5e">, { kind: "stat-cap-override" }>
): boolean => {
  if (c.when === undefined) {
    return true;
  }
  if (classifyPredicate(c.when) !== "hydration-stable") {
    return false;
  }
  return evaluate(c.when, buildHydrationPredicateContext(input));
};

export function reduceStatCapOverride(
  input: ReducerInput<"dnd5e">
): ReducerOutput<"dnd5e"> {
  return {
    target: "statBlock",
    patch: {
      mode: "resolve",
      fn: (sb) => {
        const abilityScoreCaps = {
          ...defaultAbilityScoreCaps(),
          ...sb.resolved.abilityScoreCaps,
        };
        let maxAttunedSlots =
          sb.resolved.maxAttunedSlots ?? DEFAULT_MAX_ATTUNED_SLOTS;

        for (const c of input.contributions) {
          if (c.kind !== "stat-cap-override" || !passesWhen(input, c)) {
            continue;
          }
          if (!Number.isFinite(c.ceiling)) {
            continue;
          }
          if (c.stat === "attunement-slots") {
            maxAttunedSlots = Math.max(maxAttunedSlots, c.ceiling);
            continue;
          }
          const prefix = "ability-score:";
          if (!c.stat.startsWith(prefix)) {
            continue;
          }
          const ability = c.stat.slice(prefix.length) as AbilityScore;
          if (!DND5E_ABILITY_SCORES.includes(ability)) {
            continue;
          }
          abilityScoreCaps[ability] = Math.max(
            abilityScoreCaps[ability],
            c.ceiling
          );
        }

        return {
          ...sb,
          resolved: {
            ...sb.resolved,
            abilityScoreCaps,
            maxAttunedSlots,
          },
        };
      },
    },
  };
}

export function registerStatCapOverrideReducer(): void {
  entityHydrationRegistry.registerReducer<"dnd5e">({
    primitive: STAT_CAP_OVERRIDE,
    reduce: reduceStatCapOverride,
    alwaysRun: true,
  });
}
